import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { FieldKey } from '../views/types';
import { cardDropTarget } from './columnSidebarOps';
import { autoScrollStep } from './dragAutoScroll';
import scrollParent from '@utils/scrollParent';

/**
 * Native HTML5 drag state for the column sidebar ("Obsah tabulky"). Ported from the RD-2957 PoC.
 *
 * Native rather than a library: it is one gesture, and a drag-and-drop dependency is not worth its
 * supply-chain surface.
 *
 * Two kinds of drop target, which is the one distinction the sidebar asks the user to make: onto a
 * column (the field stacks into it) or into the gap between columns (the field becomes a column of
 * its own). And two kinds of thing being dragged — a field or a whole column — tracked here rather
 * than read from `dataTransfer`, which browsers keep unreadable until the drop.
 *
 * Positions are those the user sees (visible columns and members); the sidebar translates them to the
 * stored columns, which may also hold fields the user may not see.
 *
 * While a drag is in flight, holding the pointer near the top or bottom of the sidebar scrolls it (see
 * `autoScrollStep`): browsers only do that for the window, so a column could otherwise only move within
 * the part of the list in view.
 */
export interface MemberTarget {
  columnId: string;
  /** Insert position among the column's visible members. */
  index: number;
}

/** A drag-over older than this means the pointer left the window; the list stops scrolling until it is back. */
const STALE_POINTER_MS = 200;

/**
 * Scrolls the dragged item's scroll area while the pointer is near one of its edges, from `start` to
 * `stop`. It reads the pointer from document drag-overs in the capture phase — the drop targets stop
 * theirs from propagating — and steps once per frame, as drag-over may fire only every 50ms or so.
 */
const useDragAutoScroll = () => {
  const stopRef = useRef<(() => void) | null>(null);

  const stop = useCallback(() => {
    stopRef.current?.();
    stopRef.current = null;
  }, []);

  const start = useCallback(
    (from: HTMLElement) => {
      stop();
      const area = scrollParent(from);
      if (!area) return;
      let pointerY: number | null = null;
      let seenAt = 0;
      const onDragOver = (event: DragEvent) => {
        pointerY = event.clientY;
        seenAt = performance.now();
      };
      let frame = 0;
      const tick = () => {
        if (pointerY !== null && performance.now() - seenAt < STALE_POINTER_MS) {
          const step = autoScrollStep(pointerY, area.getBoundingClientRect());
          if (step) area.scrollTop += step;
        }
        frame = requestAnimationFrame(tick);
      };
      document.addEventListener('dragover', onDragOver, true);
      frame = requestAnimationFrame(tick);
      stopRef.current = () => {
        document.removeEventListener('dragover', onDragOver, true);
        cancelAnimationFrame(frame);
      };
    },
    [stop],
  );

  useEffect(() => stop, [stop]);

  return { start, stop };
};

export const useColumnDnd = () => {
  const autoScroll = useDragAutoScroll();
  const draggedKeyRef = useRef<FieldKey | null>(null);
  const draggedColumnRef = useRef<string | null>(null);
  // The field of a dragged one-field column: dropped onto another column, it stacks there like a field.
  const draggedSoleKeyRef = useRef<FieldKey | null>(null);
  const [draggedKey, setDraggedKey] = useState<FieldKey | null>(null);
  const [draggedColumn, setDraggedColumn] = useState<string | null>(null);
  const [memberTarget, setMemberTargetState] = useState<MemberTarget | null>(null);
  const [gapIndex, setGapIndexState] = useState<number | null>(null);
  // The drop reads the targets from refs: a quick drop can follow a single drag-over before React has
  // re-rendered with the state that drag-over set. The state only drives the drop indicator.
  const memberTargetRef = useRef<MemberTarget | null>(null);
  const gapIndexRef = useRef<number | null>(null);
  const setMemberTarget = useCallback((next: MemberTarget | null | ((current: MemberTarget | null) => MemberTarget | null)) => {
    const value = typeof next === 'function' ? next(memberTargetRef.current) : next;
    memberTargetRef.current = value;
    setMemberTargetState(value);
  }, []);
  const setGapIndex = useCallback((next: number | null | ((current: number | null) => number | null)) => {
    const value = typeof next === 'function' ? next(gapIndexRef.current) : next;
    gapIndexRef.current = value;
    setGapIndexState(value);
  }, []);

  const startFieldDrag = useCallback((event: React.DragEvent, key: FieldKey) => {
    // A member sits inside a draggable column card; without this the browser drags the whole column.
    event.stopPropagation();
    draggedKeyRef.current = key;
    draggedColumnRef.current = null;
    draggedSoleKeyRef.current = null;
    setDraggedKey(key);
    setDraggedColumn(null);
    autoScroll.start(event.currentTarget as HTMLElement);
    event.dataTransfer.effectAllowed = 'move';
    // Some browsers cancel a drag that carries no payload.
    event.dataTransfer.setData('text/plain', key);
  }, []);

  /** `soleKey`: the column holds this one field (as the user sees it). */
  const startColumnDrag = useCallback((event: React.DragEvent, columnId: string, soleKey?: FieldKey) => {
    draggedColumnRef.current = columnId;
    draggedSoleKeyRef.current = soleKey ?? null;
    draggedKeyRef.current = null;
    setDraggedColumn(columnId);
    setDraggedKey(null);
    autoScroll.start(event.currentTarget as HTMLElement);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', columnId);
  }, []);

  const endDrag = useCallback(() => {
    autoScroll.stop();
    draggedKeyRef.current = null;
    draggedColumnRef.current = null;
    draggedSoleKeyRef.current = null;
    setDraggedKey(null);
    setDraggedColumn(null);
    setMemberTarget(null);
    setGapIndex(null);
  }, []);

  /** Over a member row: the field lands above or below it, by which half is hovered. */
  const hoverMember = useCallback((event: React.DragEvent, columnId: string, index: number) => {
    // Only a field can go inside a column — a one-field column counts as its field. A column of several
    // dropped on a column would mean "merge", which the model doesn't have.
    if (!draggedKeyRef.current && !(draggedSoleKeyRef.current && draggedColumnRef.current !== columnId)) return;
    event.preventDefault();
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    setGapIndex(null);
    setMemberTarget({ columnId, index: event.clientY > rect.top + rect.height / 2 ? index + 1 : index });
  }, []);

  /** Over a column card outside its member rows: see `cardDropTarget`. */
  const hoverColumn = useCallback((event: React.DragEvent, columnId: string, memberCount: number, columnIndex: number) => {
    const column = draggedColumnRef.current;
    if (!column && !draggedKeyRef.current) return;
    event.preventDefault();
    const rect = event.currentTarget.getBoundingClientRect();
    const drop = cardDropTarget({
      drag: column ? (draggedSoleKeyRef.current ? 'singleFieldColumn' : 'column') : 'field',
      self: column === columnId,
      fraction: rect.height > 0 ? (event.clientY - rect.top) / rect.height : 0,
      columnId,
      columnIndex,
      memberCount,
    });
    if (drop.kind === 'gap') {
      setMemberTarget(null);
      setGapIndex(current => (current === drop.index ? current : drop.index));
      return;
    }
    setGapIndex(null);
    setMemberTarget(current => (current?.columnId === drop.target.columnId && current.index === drop.target.index ? current : drop.target));
  }, []);

  /** Over the strip between two columns: a field becomes a new column, a column moves there. */
  const hoverGap = useCallback((event: React.DragEvent, index: number) => {
    if (!draggedKeyRef.current && !draggedColumnRef.current) return;
    event.preventDefault();
    event.stopPropagation();
    setMemberTarget(null);
    setGapIndex(current => (current === index ? current : index));
  }, []);

  /** Resolves the drop for whichever gesture is in flight; a target without a matching handler just ends it. */
  const commitDrop = useCallback(
    (
      event: React.DragEvent,
      handlers: {
        onFieldToColumn?: (key: FieldKey, target: MemberTarget) => void;
        onFieldToGap?: (key: FieldKey, gap: number) => void;
        onColumnToGap?: (columnId: string, gap: number) => void;
      },
    ) => {
      event.preventDefault();
      event.stopPropagation();
      const key = draggedKeyRef.current;
      const columnId = draggedColumnRef.current;
      const soleKey = draggedSoleKeyRef.current;
      const gap = gapIndexRef.current;
      const target = memberTargetRef.current;
      if (columnId && gap !== null) handlers.onColumnToGap?.(columnId, gap);
      else if (key && target) handlers.onFieldToColumn?.(key, target);
      else if (columnId && soleKey && target) handlers.onFieldToColumn?.(soleKey, target);
      else if (key && gap !== null) handlers.onFieldToGap?.(key, gap);
      endDrag();
    },
    [endDrag],
  );

  return { draggedKey, draggedColumn, memberTarget, gapIndex, startFieldDrag, startColumnDrag, endDrag, hoverMember, hoverColumn, hoverGap, commitDrop };
};

export default useColumnDnd;
